import React, { useState, useEffect } from 'react'
import { accent, cardStyle, textPrimary, bgMuted } from './styles'
import { formatYear, getApiUrl } from './gameLogic'

function PlayerGameModal({ playerName, date, strikes, accuracy, wager, payout, weeklyBank, placedEventIds = [], onClose }) {
  const [dailyPuzzle, setDailyPuzzle] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const apiBaseUrl = getApiUrl()

  useEffect(() => {
    const fetchDailyPuzzle = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/eventpro/daily/?date=${date}`)
        if (res.ok) {
          const data = await res.json()
          setDailyPuzzle(data)
        }
      } catch (err) {
        console.error('Failed to load puzzle:', err)
        setError('Could not load puzzle data')
      } finally {
        setLoading(false)
      }
    }

    fetchDailyPuzzle()
  }, [date, apiBaseUrl])

  // Get placed events in order they were placed
  const getOrderedPlacedEvents = () => {
    if (!dailyPuzzle || !placedEventIds || placedEventIds.length === 0) {
      return dailyPuzzle?.events || []
    }
    // Order events based on placedEventIds
    const eventMap = {}
    dailyPuzzle.events.forEach((e) => {
      eventMap[e.id] = e
    })
    return placedEventIds.map((id) => eventMap[id]).filter(Boolean)
  }

  if (loading) {
    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '32px', textAlign: 'center' }}>
          Loading...
        </div>
      </div>
    )
  }

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
      <div style={{ backgroundColor: 'white', borderRadius: '12px', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflow: 'auto', padding: '24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.2rem', color: textPrimary, fontWeight: '600' }}>{playerName}</h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#666' }}>
              {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#999', padding: 0 }}
          >
            ✕
          </button>
        </div>

        {/* Stats Summary */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          <div style={{ padding: '12px', backgroundColor: '#f0fdf4', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '0.7rem', color: '#999', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
              Score
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '900', color: accuracy === 4 ? accent : '#666' }}>
              {accuracy}/4
            </div>
          </div>
          <div style={{ padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '0.7rem', color: '#999', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
              Payout
            </div>
            <div style={{ fontSize: '1.3rem', fontWeight: '900', color: accent }}>
              ${payout ? parseFloat(payout).toFixed(2) : '0.00'}
            </div>
          </div>
        </div>

        {/* Strike Grid */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#666', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Guesses
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            {strikes && strikes.map((isStrike, idx) => (
              <div
                key={idx}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '8px',
                  backgroundColor: isStrike ? '#fef2f2' : '#f0fdf4',
                  border: `2px solid ${isStrike ? '#dc2626' : '#16a34a'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  fontWeight: '900',
                }}
              >
                {isStrike ? '🔴' : '🟢'}
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        {dailyPuzzle && (
          <div style={{ backgroundColor: '#fafafa', borderRadius: '8px', padding: '16px' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>
              Timeline
            </div>

            {(() => {
              // Combine anchor and placed events, sort chronologically
              const orderedPlaced = getOrderedPlacedEvents()
              const allEvents = [
                { ...dailyPuzzle.anchor, isAnchor: true, guessNumber: null },
                ...orderedPlaced.map((event, idx) => ({ ...event, isAnchor: false, guessNumber: idx + 1 }))
              ]
              const sorted = allEvents.sort((a, b) => b.year - a.year)

              return sorted.map((event) => {
                const wasStrike = event.guessNumber ? strikes[event.guessNumber - 1] : false
                const borderColor = event.isAnchor ? accent : (wasStrike ? '#dc2626' : '#16a34a')
                return (
                  <div
                    key={event.id}
                    style={{
                      padding: '12px',
                      backgroundColor: event.isAnchor ? '#fff7ed' : 'white',
                      borderRadius: '8px',
                      borderLeft: `4px solid ${borderColor}`,
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '0.8rem', fontWeight: '600', color: textPrimary, marginBottom: '2px' }}>
                        {event.title}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#999' }}>
                        {event.isAnchor ? 'Anchor' : `Guess #${event.guessNumber}`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ fontSize: '1.2rem', fontWeight: '900', color: accent }}>
                        {formatYear(event.year)}
                      </div>
                      {!event.isAnchor && (
                        <div style={{ fontSize: '1.3rem' }}>
                          {wasStrike ? '🔴' : '🟢'}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })
            })()}
          </div>
        )}

        {error && (
          <div style={{ padding: '12px', backgroundColor: '#fef2f2', borderRadius: '8px', color: '#dc2626', fontSize: '0.85rem', marginTop: '20px' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  )
}

export default PlayerGameModal
